import HttpClient from './http.client';
import { Method } from '../types/http/Method';
import { encodeHttpFilter } from '../utils/http.filter';
import { IDefaultDestination } from '../types/IDefaultDestination';
import { IPathFinder } from '../types/IPathFinder';
import { IQueryParams } from '../types/IQueryParams';
import { IBuilding } from '../types/IBuilding';
import { ILanguage } from "../types/ILanguage";
import { ICrossReffernce, INode, INodesDestinationMeta } from "../types/INode";
import { IUiContent } from "../types/IUiContent";
import { IGettingToSettings } from "../types/IGettingToSettings";
import { IDestinationCategory } from "../types/IDestinationCategory";
import { getNodeAndDestinationIdFromPkid, getNodeIdFromPkidOrId } from '../utils/nodeDestinationUtils';
import { analytics } from '../index';
import _ from 'lodash';
import { AnalyticsEvents } from '../store/AnalyticsStore';
import { GettingToPageQueryParams } from '../types/queryParams/GettingTo';


class Api {

    public static getBuildingById = async (id: string) => {
        const filter = encodeHttpFilter({ include: 'buildingMobileLanguages' });
        const url = `/Buildings/${id}?filter=${filter}`;
        return await HttpClient.loadData<IBuilding>(Method.GET, url);
    };

    public static getMultiBuildings = async (ids: string[]) => {
        const filter = encodeHttpFilter({ where: { "id": { "inq": ids } }, include: 'buildingMobileLanguages' });
        const url = `/Buildings?filter=${filter}`;
        return await HttpClient.loadData<IBuilding[]>(Method.GET, url);
    };

    public static getDefaultDestinations = async () => {
        const filter = encodeHttpFilter({ include: ['defaultDestinationNames', 'destinationBuildings'] });
        const url = `/Default_Destinations?filter=${filter}`;
        return await HttpClient.loadData<IDefaultDestination[]>(Method.GET, url);
    };

    public static getAllLanguages = async () => {
        const filter = encodeHttpFilter({ include: 'languageNameTranslations' });
        const url = `/Languages?filter=${filter}`;
        return await HttpClient.loadData<ILanguage[]>(Method.GET, url);
    };

    public static getNodesByBuildingId = async (id: string) => {
        const filter = encodeHttpFilter({ include: ['names', 'destinations'] });
        const url = `/Buildings/${id}/models/Node?filter=${filter}`;
        return await HttpClient.loadData<INode[]>(Method.GET, url);
    };

    public static getDestinationNodesByBuildingId = async (id: string) => {
        const url = `/Buildings/destination-nodes?buildingId=${id}&v=2`;
        return await HttpClient.loadData<INodesDestinationMeta[]>(Method.GET, url);
    };

    public static getCrossRefferencesByBuildingId = async (id: string) => {
        const filter = encodeHttpFilter({ where: { buildingId: id }, include: ['followupLocation'] })
        const url = `/CrossReferences?filter=${filter}`;
        return await HttpClient.loadData<ICrossReffernce[]>(Method.GET, url)
    }

    public static fetchPathFinder = async (params: IQueryParams) => {
        const { buildingId, nodeFrom, nodeTo, routeLang, WheelChair, channel, homepage } = params;
        if (!buildingId || !nodeFrom || !nodeTo) return;
        if (nodeFrom === nodeTo)
            return;

        const [nodeFromId, destinationFromId] = getNodeAndDestinationIdFromPkid(nodeFrom);
        const [nodeToId, destinationToId] = getNodeAndDestinationIdFromPkid(nodeTo);
        const sessionUUID = localStorage.getItem('sessionUUID');
        const timestamp = Date.now()

        const filter = _.omitBy({
            buildingId,
            routeLang,
            isWheelchair: WheelChair,
            channel: channel,
            homepage: homepage ? homepage : undefined,
            nodeFrom: nodeFromId,
            destinationFrom: destinationFromId,
            nodeTo: nodeToId,
            destinationTo: destinationToId,
            isPhotoRoute: "true",
            sessionUUID,
        }, _.isNil) as Record<string, string>

        const url = '/Buildings/pathfinder' + '?' + new URLSearchParams(filter).toString()
        
        const timeFrom = Date.now()
        const res = await HttpClient.loadData<IPathFinder>(Method.GET, url);
        const timeTo = Date.now()
        
        analytics.logEvent(AnalyticsEvents.RouteChange, {
            sessionUUID,
            timestamp,
            from: _.filter([nodeFromId, destinationFromId], _.identity).join('-'),
            to: _.filter([nodeToId, destinationFromId], _.identity).join('-'),
            loadTime: timeTo - timeFrom,
            statisticId: res.statisticId,
            distance: res.distance,
            photoCount: _.size(res.data),
            buildingId: res.buildingId,
        })

        return res
    };

    public static getDefaultContents = async () => {
        const filter = encodeHttpFilter({ include: 'defaultUiNames' });
        const url = `/Default_Uis?filter=${filter}`;
        return await HttpClient.loadData<IUiContent[]>(Method.GET, url);
    };

    public static getShortLink = async (url: string) => {
        // const PROXY_URL = 'https://cors-anywhere.herokuapp.com/';
        const options = `https://tinyurl.com/api-create.php?url=${encodeURIComponent(url)}`;
        return await HttpClient.loadText(Method.GET, options);
    };

    public static getEmailTemplates = async () => {
        const url = `/EmailTemplates`;
        return await HttpClient.loadData<[]>(Method.GET, url);
    };

    public static sendEmail = async (result: {}) => {
        const url = `/Buildings/pathfinder/email`;
        return await HttpClient.loadData<{}>(Method.POST, url, result);
    };

    public static getGettingToSettings = async (params: Partial<GettingToPageQueryParams>) => {
        const filter = encodeHttpFilter({ where: {
            "facilityLocationId": params.facilityLocationId,
            "buildingId": params.buildingId,
            "destinationId": params.destinationId,
        }, include: ["address"]
        });
        const url = `/GettingToSettings?filter=${filter}`;
        return await HttpClient.loadData<IGettingToSettings[]>(Method.GET, url);
    }

    public static getDestinationCategoriesByBuildingId = async (buildingId: string) => {
        const filter = encodeHttpFilter({ where: { "building_id": buildingId } });
        const url = `/Destination_Categories?filter=${filter}`;
        return await HttpClient.loadData<IDestinationCategory[]>(Method.GET, url);
    }

    public static patchStatistic = async (statisticsId: number, data: { found: boolean }) => {
        const url = `/Statistics/${statisticsId}`;
        // only the session that asked for the route may answer for it
        const sessionUUID = localStorage.getItem('sessionUUID');

        return await HttpClient.loadData<IDestinationCategory[]>(Method.PATCH, url, { ...data, sessionUUID });
    }
}

export default Api;
